import { useMemo, useState } from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { useAlert } from "react-alert";

import * as React from "react";

import * as Sentry from "@sentry/browser";

import { makeRequest } from "../../../util";
import { fetchEventDefs } from "../../../actions/eventDefs";

import { LoadedEventDefinition } from "../../../types/global";
import { EditTagsModal } from "./EditTagsModal";
import MenuButton from "../../../components/MenuButton";
import BulkOperationConfirmationModal from "./modals/BulkOperationConfirmationModal";

interface BulkEditTagsMenuProps {
  allEvents: LoadedEventDefinition[];
  selectedEvents: string[];
}

export function BulkEditTagsMenu({
  allEvents,
  selectedEvents,
}: BulkEditTagsMenuProps) {
  const dispatch = useDispatch();
  const alert = useAlert();

  const [editTagsModalOpen, setEditTagsModalOpen] = useState<boolean>(false);
  const [confirmEditTagsModalOpen, setConfirmEditTagsModalOpen] = useState<
    boolean
  >(false);
  const [tagsToAdd, setTagsToAdd] = useState<Record<string, true>>({});

  const allTags = useMemo(() => {
    return _(allEvents)
      .flatMap((ev) => _.keys(ev.tags))
      .uniq()
      .sortBy()
      .map((tag) => [tag, true])
      .fromPairs()
      .value();
  }, [allEvents]);

  const selectedTags = useMemo(() => {
    const eventsById = _.keyBy(allEvents, (e) => e.id);

    return _(allTags)
      .keys()
      .filter((tag) => {
        return _.every(selectedEvents, (id) => {
          return tag in eventsById[id].tags;
        });
      })
      .map((tag) => [tag, true])
      .fromPairs()
      .value();
  }, [allEvents, allTags, selectedEvents]);

  function handleSave() {
    const tagsToRemove = _.keys(selectedTags).filter(
      (tag) => !(tag in tagsToAdd)
    );

    makeRequest("bulk_edit_tags", {
      selected_events: selectedEvents,
      tags_to_add: tagsToAdd,
      tags_to_remove: tagsToRemove,
    })
      .then(() => {
        alert.info(`Updated tags for ${selectedEvents.length} events.`);
        dispatch(fetchEventDefs());
        setEditTagsModalOpen(false);
        setConfirmEditTagsModalOpen(false);
        setTagsToAdd({});
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
        alert.error("Something went wrong. Unable to edit tags.");
      });
  }

  return (
    <>
      {editTagsModalOpen && (
        <EditTagsModal
          numEvents={selectedEvents.length}
          prevAllTags={allTags}
          prevSetTags={selectedTags}
          onClose={() => setEditTagsModalOpen(false)}
          onSave={(tags: Record<string, true>) => {
            setTagsToAdd(tags);

            if (selectedEvents.length >= 5) {
              setEditTagsModalOpen(false);
              setConfirmEditTagsModalOpen(true);
              return;
            }

            handleSave();
          }}
        />
      )}

      {confirmEditTagsModalOpen && (
        <BulkOperationConfirmationModal
          actionVerb="edit tags"
          selectedEventIDs={selectedEvents}
          onClose={() => {
            setConfirmEditTagsModalOpen(false);
            setTagsToAdd({});
          }}
          onConfirm={() => {
            handleSave();
          }}
        />
      )}

      <MenuButton
        showArrow={false}
        menuID="edit-tags-menu"
        label="Edit Tags"
        onClick={() => setEditTagsModalOpen(true)}
      />
    </>
  );
}
